/**
 * Whether a page is on a developer's own machine: the only place the widget
 * writes to the console. On a visitor's page it is a guest, and a console it
 * does not own stays clean.
 *
 * Exact names, not a pattern (review of TOADO-587): matching "localhost"
 * anywhere in a host warned on `notlocalhost.com` and stayed silent on IPv6's
 * `[::1]`. The machine itself is `localhost` (and its subdomains), any IPv4
 * loopback address (127.0.0.0/8, which some set-ups use for a second origin),
 * or IPv6's `::1`; a `.local` name is a machine on the developer's own network.
 * A name written with its final dot (`localhost.`) is the same name.
 *
 * Not included: public DNS names that resolve to loopback (`127.0.0.1.nip.io`).
 * A name is judged by what it says, and anyone can serve a site under nip.io.
 */
export function onDeveloperHost(hostname: string = window.location.hostname): boolean {
  const host = hostname.toLowerCase().replace(/\.$/, '');
  return host === 'localhost'
    || host.endsWith('.localhost')
    || loopbackIPv4(host)
    || host === '[::1]'
    || host === '::1'
    || host.endsWith('.local');
}

/** 127.0.0.0/8, written as four decimal octets and nothing else. */
function loopbackIPv4(host: string): boolean {
  const octets = host.split('.');
  return octets.length === 4
    && octets[0] === '127'
    && octets.every((octet) => /^\d{1,3}$/.test(octet) && Number(octet) <= 255);
}
